import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

function nativeEventType(event) {
  return (event.native && event.native.type) || event.type || '';
}

function isTouchEvent(event) {
  const native = event.native;
  if (!native) return false;
  if (native.pointerType === 'touch') return true;
  return nativeEventType(event).indexOf('touch') === 0;
}

function isTouchScrubEvent(event) {
  const type = nativeEventType(event);
  return type === 'touchstart' || type === 'touchmove' || type === 'pointerdown' || type === 'pointermove';
}

function nearestScrubIndex(chart, x) {
  const meta = chart.getDatasetMeta(0);
  if (!meta || !meta.data.length) return null;

  let nearest = 0;
  let best = Infinity;
  meta.data.forEach((el, i) => {
    if (!el || el.skip) return;
    const distance = Math.abs(el.x - x);
    if (distance < best) {
      best = distance;
      nearest = i;
    }
  });
  return nearest;
}

function applyScrub(chart, index, lineX) {
  const meta = chart.getDatasetMeta(0);
  const el = meta && meta.data[index];
  const x = lineX != null ? lineX : (el && el.x);
  if (index == null || x == null) return;

  chart.$scrubIndex = index;
  chart.$scrubX = x;
  const active = [{ datasetIndex: 0, index }];
  chart.setActiveElements(active);
  if (chart.tooltip) {
    chart.tooltip.setActiveElements(active, { x, y: el ? el.y : 0 });
  }
}

function freezeScrub(chart) {
  applyScrub(chart, chart.$scrubIndex);
}

// Finger/mouse scrubber: a vertical line tracks pointer X while the
// nearest data point (by X) is highlighted and shown in the tooltip.
//
// Chart.js remaps touchend → mouseup (and often reports x=0). If that
// event reaches Chart.js hover handling, it selects the leftmost point.
const historyScrubPlugin = {
  id: 'historyScrub',
  beforeEvent(chart, args) {
    const event = args.event;
    if (!event) return;

    if (isTouchEvent(event)) chart.$scrubSticky = true;

    // Skip Chart.js hover for finger-up and the click/mousemove it
    // synthesizes; those would overwrite the last selected point.
    if (chart.$scrubSticky && !isTouchScrubEvent(event)) {
      freezeScrub(chart);
      return false;
    }
  },
  afterEvent(chart, args) {
    const event = args.event;
    if (!event || !chart.chartArea) return;

    if (isTouchEvent(event)) chart.$scrubSticky = true;

    if (chart.$scrubSticky && !isTouchScrubEvent(event)) {
      freezeScrub(chart);
      args.changed = true;
      return;
    }

    if (event.type === 'mouseout') {
      chart.$scrubX = null;
      chart.$scrubIndex = null;
      chart.setActiveElements([]);
      if (chart.tooltip) chart.tooltip.setActiveElements([], { x: 0, y: 0 });
      args.changed = true;
      return;
    }

    if (event.x == null) return;

    const area = chart.chartArea;
    const x = Math.max(area.left, Math.min(area.right, event.x));
    const nearest = nearestScrubIndex(chart, x);
    if (nearest == null) return;

    applyScrub(chart, nearest, x);
    args.changed = true;
  },
  afterDraw(chart) {
    const x = chart.$scrubX;
    const area = chart.chartArea;
    if (x == null || !area) return;

    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 3]);
    ctx.moveTo(x, area.top);
    ctx.lineTo(x, area.bottom);
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.45)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  },
};

const HistoryCanvas = (props) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Clean up previous chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const history = props.test.history;
    const bounds = props.test.bounds;
    const limits = [props.test?.min, props.test?.max];
    
    // Debug: log the limits to see if they exist
    console.log('Test limits:', limits);
    console.log('Test data:', props.test);

    // History is previous visits only. Always plot this report's result as
    // the rightmost "current" point — unless this visit is already in the
    // series (submitted reports used to include it, which doubled the point).
    const currentDate = new Date(props.onsite).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
    const historyItems = (history || []).map((item, index) => ({
      result: item.result,
      date: item.date,
      index,
      isCurrent: !!item.current,
    }));
    const existingCurrent = [...historyItems].reverse().find(item =>
      item.isCurrent || (item.result === props.test.result && item.date === currentDate)
    );
    if (existingCurrent) {
      existingCurrent.isCurrent = true;
    }
    const currentItem = existingCurrent ? null : {
      result: props.test.result,
      date: currentDate,
      index: historyItems.length,
      isCurrent: true,
    };
    const validData = historyItems
      .concat(currentItem || [])
      .filter(item => item.result !== null && item.result !== undefined);

    if (validData.length === 0) {
      return;
    }

    // Calculate tolerance for "slightly outside" (15% of the range)
    const minLimit = limits[0];
    const maxLimit = limits[1];
    const range = maxLimit && minLimit ? maxLimit - minLimit : 0;
    const tolerance = range * 0.10;

    const dataPoints = validData.map(item => {
      let pointColor = '#22C55E'; // Default green
      let borderColor = '#ffffff'; // Default white border
      
      if ((minLimit && item.result < minLimit) || (maxLimit && item.result > maxLimit)) {
        const distanceFromLimit = Math.min(
          Math.abs(item.result - minLimit),
          Math.abs(item.result - maxLimit)
        );
        if (distanceFromLimit <= tolerance) {
          pointColor = '#F59E0B';
          borderColor = '#ffffff';
        } else {
          pointColor = '#EF4444';
          borderColor = '#ffffff';
        }
      }
      
      return {
        x: item.index,
        y: item.result,
        date: item.date,
        isCurrent: item.isCurrent || false,
        pointColor,
        borderColor,
      };
    });

    // Configure chart options
    const chartConfig = {
      type: 'line',
      plugins: [historyScrubPlugin],
      data: {
        datasets: [
          {
            label: 'Test Results',
            data: dataPoints,
            borderColor: '#22C55E',
            backgroundColor: ctx => {
              // Create dynamic gradient based on the current segment
              const chart = ctx.chart;
              const {ctx: chartCtx, chartArea} = chart;
              
              if (!chartArea) {
                return null;
              }
              
              // Get the current data index to determine gradient color
              const dataIndex = ctx.dataIndex;
              const currentPoint = dataPoints[dataIndex];
              
              // Determine base color based on current point's status
              let baseColor = '#22C55E'; // Default green
              if (currentPoint && currentPoint.pointColor) {
                baseColor = currentPoint.pointColor;
              }
              
              // Create gradient
              const gradient = chartCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              let rgbaColor, transparentColor;
              
              if (baseColor === '#22C55E') {
                rgbaColor = 'rgba(34, 197, 94, 0.3)';
                transparentColor = 'rgba(34, 197, 94, 0.0)';
              } else if (baseColor === '#F59E0B') {
                rgbaColor = 'rgba(245, 158, 11, 0.3)';
                transparentColor = 'rgba(245, 158, 11, 0.0)';
              } else {
                rgbaColor = 'rgba(239, 68, 68, 0.3)';
                transparentColor = 'rgba(239, 68, 68, 0.0)';
              }
              
              gradient.addColorStop(0, rgbaColor);
              gradient.addColorStop(1, transparentColor);
              
              return gradient;
            },
            borderWidth: 3,
            pointBackgroundColor: dataPoints.map(point => point.pointColor),
            pointBorderColor: dataPoints.map(point =>
              point.isCurrent ? '#FFD700' : point.borderColor
            ),
            pointBorderWidth: dataPoints.map(point => point.isCurrent ? 3 : 2),
            pointRadius: dataPoints.map(point => point.isCurrent ? 7 : 4),
            pointHoverRadius: 8,
            pointHoverBackgroundColor: dataPoints.map(point => {
              if (point.pointColor === '#F59E0B') return '#D97706';
              if (point.pointColor === '#EF4444') return '#DC2626';
              return '#16A34A';
            }),
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 3,
            fill: true,
            tension: 0.4,
            spanGaps: true,
            segment: {
              borderColor: ctx => {
                // Use the color of the next point for the segment
                const i = ctx.p1DataIndex;
                return dataPoints[i]?.pointColor || '#22C55E';
              },
              backgroundColor: ctx => {
                // Create dynamic gradient for each segment based on previous point
                const chart = ctx.chart;
                const {ctx: chartCtx, chartArea} = chart;
                
                if (!chartArea) {
                  return null;
                }
                
                const dataIndex = ctx.p1DataIndex;
                const currentPoint = dataPoints[dataIndex];
                
                // Determine base color based on current point's status
                let baseColor = '#22C55E'; // Default green
                if (currentPoint && currentPoint.pointColor) {
                  baseColor = currentPoint.pointColor;
                }
                
                // Create gradient for this segment
                const gradient = chartCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                let rgbaColor, transparentColor;
                
                if (baseColor === '#22C55E') {
                  rgbaColor = 'rgba(34, 197, 94, 0.3)';
                  transparentColor = 'rgba(34, 197, 94, 0.0)';
                } else if (baseColor === '#F59E0B') {
                  rgbaColor = 'rgba(245, 158, 11, 0.3)';
                  transparentColor = 'rgba(245, 158, 11, 0.0)';
                } else {
                  rgbaColor = 'rgba(239, 68, 68, 0.3)';
                  transparentColor = 'rgba(239, 68, 68, 0.0)';
                }
                
                gradient.addColorStop(0, rgbaColor);
                gradient.addColorStop(1, transparentColor);
                
                return gradient;
              }
            }
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove', 'touchend', 'touchcancel'],
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        hover: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#22C55E',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
            // Plugin drives show/hide so a tap is not dismissed by mouseout.
            events: ['mousemove', 'touchstart', 'touchmove'],
            callbacks: {
              title: function(context) {
                const dataPoint = dataPoints[context[0].dataIndex];
                if (!dataPoint?.date) return 'No Date';

                const monthNames = [
                  'Jan.', 'Feb.', 'Mar.', 'Apr.', 'May.', 'Jun.',
                  'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'
                ];

                // Handles "m/d" (history) and "m/d/yy" (current reading)
                const dateParts = dataPoint.date.split('/');
                const month = parseInt(dateParts[0]);
                const day = parseInt(dateParts[1]);
                const monthName = monthNames[month - 1] || `Month ${month}`;
                const label = `${monthName} ${day}`;
                return dataPoint.isCurrent ? `${label} (current)` : label;
              },
              label: function(context) {
                return `Result: ${context.parsed.y}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            display: false,
            grid: {
              display: false,
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false
            },
            ticks: {
              stepSize: 1,
              color: '#6B7280',
              font: {
                size: 10
              },
              callback: function(value) {
                return `#${value + 1}`;
              }
            },
            title: {
              display: false,
            }
          },
          y: {
            display: true,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false
            },
            ticks: {
              color: '#6B7280',
              font: {
                size: 10
              },
              callback: function(value) {
                return value.toFixed(1);
              }
            },
            title: {
              display: false,
            },
            // Ensure the scale includes the limit values
            beginAtZero: false
          }
        },
        elements: {
          point: {
            hoverRadius: 6
          }
        }
      }
    };


    // Create the chart
    chartRef.current = new Chart(canvas, chartConfig);

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [props.test.history, props.test.bounds, props.test.min, props.test.max, props.test.result, props.onsite]);

  return (
    <div className="test-panel history-container">
      <div className="chart-container">
        <canvas
          ref={canvasRef}
          id={`history-${props.test.testId}`}
          width="300"
          height="88"
          className="result-history"
        />
      </div>

      <div className="data-summary">
        <div className="summary-item">
          <span className="summary-label">Latest</span>
          <span className="summary-value">
            {props.test.history[props.test.history.length - 1]?.result || '—'}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Average</span>
          <span className="summary-value">
            {(() => {
              const validResults = props.test.history
                .map(h => h.result)
                .filter(r => r !== null && r !== undefined);
              return validResults.length > 0 
                ? (validResults.reduce((a, b) => a + b, 0) / validResults.length).toFixed(1)
                : '—';
            })()}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Tests</span>
          <span className="summary-value">
            {props.test.history.filter(h => h.result !== null && h.result !== undefined).length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default HistoryCanvas;